import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';
import { useMe } from './MeContext';

/**
 * Returns the auth token + active tenant code + current warehouse id for the
 * current session.
 *
 * Used by tenant-scoped API hooks (`useTenantAdmin*`) that need to set
 * `Authorization`, `X-Tenant-Id`, and `X-Warehouse-Id` headers. Token and
 * tenantCode are `null` until `MeContext` resolves to `ok`; the calling hook
 * should gate `enabled` on both being non-null.
 *
 * `warehouseId` resolution (SP-6g): first `localStorage['vp.current_warehouse_id']`
 * (written by the `WarehouseSelector` when the user picks a warehouse), then
 * the `/me` payload — `default_warehouse.id` (DB-enforced `is_default=true`),
 * then `warehouse.id`, then the **first** `warehouses[]` grant. The last fallback
 * covers users who hold warehouse grants but have NO `is_default` membership:
 * without it they'd be stuck with `warehouseId === null` and a dead-end
 * "Seleziona un magazzino" with no selector in the header. May still be `null`
 * for users without any warehouse binding (e.g. tenant-admin only).
 *
 * The first time we resolve a `/me`-derived value with an empty localStorage,
 * we **seed** the storage so other readers (and the header selector) stay
 * coherent. Previously the seed only happened on an explicit selector change,
 * which left single-warehouse users with `warehouseId === null` and broke
 * pages that need an explicit warehouse (e.g. the assignment board).
 */
export function useTenantContext(): { token: string | null; tenantCode: string | null; warehouseId: number | null } {
  const auth = useAuth();
  const { state } = useMe();
  const token = auth.user?.access_token ?? null;
  const tenantCode = state.kind === 'ok' ? state.me.tenant?.code ?? null : null;

  const storedRaw = typeof window !== 'undefined'
    ? localStorage.getItem('vp.current_warehouse_id')
    : null;
  const storedId = storedRaw ? Number(storedRaw) : null;
  const fromMe = state.kind === 'ok'
    ? state.me.default_warehouse?.id
      ?? state.me.warehouse?.id
      // SP-6g fix: utenti con grant su magazzini ma SENZA `is_default`
      // (default_warehouse null) altrimenti restano con warehouseId null →
      // dead-end "Seleziona un magazzino" con nessun selettore in header.
      // Ripieghiamo sul primo magazzino di cui l'utente è membro.
      ?? state.me.warehouses?.[0]?.id
      ?? null
    : null;

  // Il valore memorizzato va VALIDATO contro i grant dell'utente.
  //
  // `vp.current_warehouse_id` sopravvive al cambio di utente nello stesso
  // browser: chi entrava con un account che NON ha quel magazzino restava
  // agganciato a un id inaccessibile, e ogni chiamata warehouse-scoped tornava
  // 403 senza alcun indizio (pagine vuote, conteggi in errore). Col
  // multi-magazzino il caso passa da raro a ordinario.
  //
  // Si invalida SOLO con prova positiva: se `/me` non espone `warehouses[]` non
  // possiamo sapere se il valore è legittimo, quindi lo si rispetta.
  const grantedIds = state.kind === 'ok'
    ? (state.me.warehouses ?? []).map((w: { id: number }) => w.id)
    : [];
  const storedIsStale = storedId !== null
    && grantedIds.length > 0
    && !grantedIds.includes(storedId);
  const warehouseId = (storedIsStale ? null : storedId) ?? fromMe;

  // Allinea localStorage: lo semina quando è vuoto e lo CORREGGE quando è
  // stantio, così anche i lettori diretti (il selettore in header) convergono.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (storedId !== null && !storedIsStale) return;
    if (fromMe !== null) {
      localStorage.setItem('vp.current_warehouse_id', String(fromMe));
    } else if (storedIsStale) {
      // Nessun magazzino da /me: meglio nessun valore che uno inaccessibile.
      localStorage.removeItem('vp.current_warehouse_id');
    }
  }, [storedId, storedIsStale, fromMe]);

  return { token, tenantCode, warehouseId };
}
